import {
  X,
  Mail,
  Building2,
  Shield,
  LogOut,
  Settings,
  Bell,
  CheckSquare,
  FileText,
  Languages,
  Target,
  BookOpen,
  Unplug,
  Users as UsersIcon,
  KeyRound,
  Building,
  Trash2,
  GitBranch,
  LifeBuoy,
  Activity,
  HeartPulse,
  FlaskConical,
  ClipboardList,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import './RightDrawer.css'

interface RightDrawerProps {
  isOpen: boolean
  onClose: () => void
  userName?: string
  userEmail?: string
  userCompany?: string
  userRole?: string
  userAvatar?: string
  userId?: string
  businessLevel?: string
  functionalRoles?: string[]
  divisionName?: string
  subdivisionName?: string
  businessEntityNames?: string[]
  entraObjectId?: string
  entraTenantId?: string
  entraPreferredUsername?: string
  entraName?: string
  entraIdentityProvider?: string
  onLogout?: () => void
  onSettings?: () => void
}

export default function RightDrawer({
  isOpen,
  onClose,
  userName = 'Guest User',
  userEmail = 'user@example.com',
  userCompany = 'Organization',
  userRole = 'User',
  userAvatar,
  userId,
  businessLevel,
  functionalRoles,
  divisionName,
  subdivisionName,
  businessEntityNames,
  entraObjectId,
  entraTenantId,
  entraPreferredUsername,
  entraName,
  entraIdentityProvider,
  onLogout,
  onSettings
}: RightDrawerProps) {
  const navigate = useNavigate()
  const complianceSpikeUi = import.meta.env.VITE_COMPLIANCE_SPIKE_UI === 'true'

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <>
      {/* Overlay */}
      <div 
        className={`drawer-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`right-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h3 className="drawer-title">Profile</h3>
          <button className="drawer-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="drawer-content">
          {/* User Profile Card */}
          <div className="profile-card">
            <div className="profile-avatar-large">
              {userAvatar ? (
                <img src={userAvatar} alt={userName} />
              ) : (
                <span className="avatar-initials-large">{getInitials(userName)}</span>
              )}
            </div>
            <h4 className="profile-name">{userName}</h4>
            <span className="profile-role">{userRole}</span>
          </div>

          {/* User Details */}
          <div className="profile-details">
            <div className="detail-item">
              <Mail size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Email</span>
                <span className="detail-value">{userEmail}</span>
              </div>
            </div>

            <div className="detail-item">
              <Building2 size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Organization</span>
                <span className="detail-value">{userCompany}</span>
              </div>
            </div>

            <div className="detail-item">
              <Shield size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Role</span>
                <span className="detail-value">{userRole}</span>
              </div>
            </div>

            <div className="detail-item">
              <Building size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Business scope</span>
                <span className="detail-value">
                  {businessLevel ?? 'Business'}
                  {divisionName ? ` | Division: ${divisionName}` : ''}
                  {subdivisionName ? ` | Subdivision: ${subdivisionName}` : ''}
                  {businessEntityNames && businessEntityNames.length > 0 ? ` | BEs: ${businessEntityNames.join(', ')}` : ''}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <KeyRound size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Entra profile (read-only)</span>
                <span className="detail-value">
                  User ID: {userId ?? '-'}
                  {entraName ? ` | Name: ${entraName}` : ''}
                  {entraPreferredUsername ? ` | UPN: ${entraPreferredUsername}` : ''}
                  {entraObjectId ? ` | OID: ${entraObjectId}` : ''}
                  {entraTenantId ? ` | Tenant: ${entraTenantId}` : ''}
                  {entraIdentityProvider ? ` | IdP: ${entraIdentityProvider}` : ''}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <Shield size={18} className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Functional roles</span>
                <span className="detail-value">
                  {functionalRoles && functionalRoles.length > 0 ? functionalRoles.join(', ') : 'Read-only (no functional roles assigned)'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="drawer-actions">
            <h5 className="actions-title">Quick Actions</h5>
            
            <button className="action-btn" onClick={onSettings}>
              <Settings size={18} />
              <span>Account Settings</span>
            </button>

            <button className="action-btn">
              <Bell size={18} />
              <span>Notification Preferences</span>
            </button>

            {userRole.toLowerCase().includes('admin') && (
              <>
                <h5 className="actions-title" style={{ marginTop: '1.5rem' }}>Admin Navigation</h5>

                <button className="action-btn" onClick={() => navigate('/rules')}>
                  <CheckSquare size={18} />
                  <span>Rules</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/pim')}>
                  <BookOpen size={18} />
                  <span>MIM Setup</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/distributors')}>
                  <Building2 size={18} />
                  <span>Distributors</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/products/orphaned')}>
                  <Unplug size={18} />
                  <span>Orphaned products</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/targets')}>
                  <Target size={18} />
                  <span>Targets</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/actions')}>
                  <CheckSquare size={18} />
                  <span>Actions</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/audit')}>
                  <FileText size={18} />
                  <span>Audit</span>
                </button>
                <button className="action-btn" onClick={() => navigate('/users')}>
                  <UsersIcon size={18} />
                  <span>User management</span>
                </button>
                {businessLevel === 'MasterAdmin' && (
                  <button className="action-btn" onClick={() => navigate('/admin/divisions')}>
                    <Building2 size={18} />
                    <span>Division management</span>
                  </button>
                )}
                <button className="action-btn" onClick={() => navigate('/master-terms')}>
                  <Languages size={18} />
                  <span>Master terms</span>
                </button>
                {businessLevel === 'MasterAdmin' && (
                  <>
                    <button className="action-btn" onClick={() => navigate('/support-tickets')}>
                      <LifeBuoy size={18} />
                      <span>Support Tickets</span>
                    </button>
                    <button className="action-btn" onClick={() => navigate('/observability')}>
                      <Activity size={18} />
                      <span>Observability</span>
                    </button>
                    <button className="action-btn" onClick={() => navigate('/health-checks')}>
                      <HeartPulse size={18} />
                      <span>Health Checks</span>
                    </button>
                    <button className="action-btn" onClick={() => navigate('/test-runner')}>
                      <FlaskConical size={18} />
                      <span>Test Runner</span>
                    </button>
                    {complianceSpikeUi && (
                      <button className="action-btn" onClick={() => navigate('/compliance-audit')}>
                        <ClipboardList size={18} />
                        <span>Compliance audit</span>
                      </button>
                    )}
                    <button className="action-btn" onClick={() => navigate('/admin/compliance-workflow')}>
                      <GitBranch size={18} />
                      <span>Compliance workflow</span>
                    </button>
                    <button className="action-btn" onClick={() => navigate('/admin/data-reset')}>
                      <Trash2 size={18} />
                      <span>Clear application data</span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="drawer-footer">
          <button className="logout-btn" onClick={onLogout}>
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </>
  )
}
